import React, { useState, useEffect } from 'react';
import { useBusinessPlan } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { getCurrencySymbol, CURRENCIES } from '../constants/currencies';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { TooltipLabel } from './ui/TooltipLabel';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import DynamicInterestFields from './financial/DynamicInterestFields';
import EquityCalculator from './financial/EquityCalculator';
import StrategicFrameworkLinker from './cost/StrategicFrameworkLinker';
import { financialManagementTooltips } from '../utils/financialTooltips';
import { 
  DollarSign, 
  Plus, 
  Edit3, 
  Trash2,
  Building2,
  CreditCard,
  PieChart,
  TrendingUp,
  AlertCircle,
  Users,
  Target,
  Info
} from 'lucide-react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/financial-management`;

const FinancialManagementV2 = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext, displayCurrency, convertCurrency } = useCurrency();
  
  // State
  const [activeTab, setActiveTab] = useState('current-debt');
  const [loading, setLoading] = useState(true);
  const [loans, setLoans] = useState([]);
  const [equityRecords, setEquityRecords] = useState([]);
  const [loanTypes, setLoanTypes] = useState({});
  const [equityTypes, setEquityTypes] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [sfHierarchy, setSfHierarchy] = useState([]);
  
  // Dialogs
  const [showLoanDialog, setShowLoanDialog] = useState(false);
  const [showEquityDialog, setShowEquityDialog] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [editingEquity, setEditingEquity] = useState(null);
  
  // Forms
  const [loanForm, setLoanForm] = useState({
    loan_name: '',
    loan_type: 'term_loan',
    custom_loan_type_name: '',  // For custom loan type
    lender_name: '',
    principal_amount: '',
    currency: currentPlan?.currency || 'USD',  // Use plan's currency as default
    interest_type: 'fixed',
    interest_rate: '',
    base_rate: '',
    margin: '',
    reference_rate_name: '',
    adjustment_frequency: '',
    rate_cap: '',
    rate_floor: '',
    term_months: '',
    payment_frequency: 'monthly',
    start_date: '',
    linked_objective_id: '',
    linked_initiative_id: '',
    linked_revenue_stream_id: '',
    owner: '',
    purpose: ''
  });

  const [equityForm, setEquityForm] = useState({
    authorized_shares: '',       // Maximum shares company can issue
    issued_shares: '',           // Shares actually issued to shareholders
    par_value: '',               // Nominal/face value per share
    additional_paid_in_capital: '', // Amount received above par value
    retained_earnings: '',       // Cumulative profits retained
    treasury_stock: '',          // Own shares repurchased
    cost_of_equity: '',          // Required return for equity investors
    currency: currentPlan?.currency || 'USD',  // Currency for equity
    
    // Strategic Framework linking
    linked_objective_id: '',
    linked_initiative_id: '',
    funding_purpose: '',         // Why this equity was raised
    investor_name: '',           // Who provided the equity
    investment_date: ''          // When equity was received
  });

  // Fetch data
  useEffect(() => {
    if (currentPlan?.id) {
      fetchAllData();
    }
  }, [currentPlan?.id]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      const [loansRes, typesRes, equityTypesRes, teamRes, sfRes] = await Promise.all([
        axios.get(`${API}/loans/${currentPlan.id}`),
        axios.get(`${API}/loan-types`),
        axios.get(`${API}/equity-types`),
        axios.get(`${BACKEND_URL}/api/enterprise/team-members?plan_id=${currentPlan.id}`),
        axios.get(`${BACKEND_URL}/api/v3/cost-management/sf-hierarchy/${currentPlan.id}`)
      ]);
      
      setLoans(loansRes.data || []);
      setLoanTypes(typesRes.data?.loan_types || {});
      setEquityTypes(equityTypesRes.data?.equity_types || {});
      setTeamMembers(teamRes.data?.team_members || []);
      setSfHierarchy(sfRes.data?.hierarchy || []);
      
      // Load equity records from backend
      try {
        const equityRes = await axios.get(`${API}/equity?plan_id=${currentPlan.id}`);
        setEquityRecords(equityRes.data || []);
      } catch (error) {
        console.log('No existing equity records found');
        setEquityRecords([]);
      }
      
      console.log('✅ Financial management data loaded');
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const openLoanDialog = () => {
    resetLoanForm();
    setEditingLoan(null);
    setShowLoanDialog(true);
  };

  const handleEditLoan = (loan) => {
    setEditingLoan(loan);
    setLoanForm({
      loan_name: loan.loan_name || '',
      loan_type: loan.loan_type || 'term_loan',
      custom_loan_type_name: loan.custom_loan_type_name || '',
      lender_name: loan.lender_name || '',
      principal_amount: loan.principal_amount?.toString() || '',
      currency: loan.currency || 'USD',
      interest_type: loan.interest_type || 'fixed',
      interest_rate: loan.interest_rate?.toString() || '',
      base_rate: loan.base_rate?.toString() || '',
      margin: loan.margin?.toString() || '',
      reference_rate_name: loan.reference_rate_name || '',
      adjustment_frequency: loan.adjustment_frequency || '',
      rate_cap: loan.rate_cap?.toString() || '',
      rate_floor: loan.rate_floor?.toString() || '',
      term_months: loan.term_months?.toString() || '',
      payment_frequency: loan.payment_frequency || 'monthly',
      start_date: loan.start_date || '',
      linked_objective_id: loan.linked_objective_id || '',
      linked_initiative_id: loan.linked_initiative_id || '',
      linked_revenue_stream_id: loan.linked_revenue_stream_id || '',
      owner: loan.owner || '',
      purpose: loan.purpose || ''
    });
    setShowLoanDialog(true);
  };

  const handleSaveLoan = async (e) => {
    e.preventDefault();
    
    try {
      const loanData = {
        plan_id: currentPlan.id,
        ...loanForm,
        principal_amount: parseFloat(loanForm.principal_amount),
        interest_rate: parseFloat(loanForm.interest_rate) || parseFloat(loanForm.base_rate) + parseFloat(loanForm.margin) || 0,
        term_months: parseInt(loanForm.term_months),
        base_rate: loanForm.base_rate ? parseFloat(loanForm.base_rate) : null,
        margin: loanForm.margin ? parseFloat(loanForm.margin) : null,
        rate_cap: loanForm.rate_cap ? parseFloat(loanForm.rate_cap) : null,
        rate_floor: loanForm.rate_floor ? parseFloat(loanForm.rate_floor) : null
      };

      if (editingLoan) {
        await axios.put(`${API}/loans/${editingLoan.id}`, loanData);
        toast.success('Loan updated successfully!');
      } else {
        await axios.post(`${API}/loans`, loanData);
        toast.success('Loan created successfully!');
      }

      setShowLoanDialog(false);
      fetchAllData();
    } catch (error) {
      console.error('Error saving loan:', error);
      toast.error(error.response?.data?.detail || 'Failed to save loan');
    }
  };

  const handleDeleteLoan = async (loanId) => {
    if (!window.confirm('Are you sure you want to delete this loan?')) return;

    try {
      await axios.delete(`${API}/loans/${loanId}`);
      toast.success('Loan deleted successfully!');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting loan:', error);
      toast.error('Failed to delete loan');
    }
  };

  const fetchEquityRecords = async () => {
    try {
      const equityRes = await axios.get(`${API}/equity?plan_id=${currentPlan.id}`);
      setEquityRecords(equityRes.data || []);
    } catch (error) {
      console.log('Error fetching equity records:', error);
      setEquityRecords([]);
    }
  };

  const handleSaveEquity = async () => {
    try {
      // Calculate total equity investment correctly
      const issuedShares = parseFloat(equityForm.issued_shares) || 0;
      const parValue = parseFloat(equityForm.par_value) || 0;
      const additionalPaidIn = parseFloat(equityForm.additional_paid_in_capital) || 0;
      const commonStock = issuedShares * parValue;
      const totalEquityInvested = commonStock + additionalPaidIn;
      
      const equityData = {
        ...equityForm,
        plan_id: currentPlan.id,
        user_id: currentPlan.user_id,
        
        // Calculate investment_amount correctly (Common Stock + Additional Paid-in)
        investment_amount: totalEquityInvested,
        
        // Also save as total_equity_value for compatibility with equity_structure collection
        total_equity_value: totalEquityInvested,
        
        // Convert numeric strings to numbers
        authorized_shares: parseFloat(equityForm.authorized_shares) || 0,
        issued_shares: issuedShares,
        par_value: parValue,
        additional_paid_in_capital: additionalPaidIn,
        retained_earnings: parseFloat(equityForm.retained_earnings) || 0,
        treasury_stock: parseFloat(equityForm.treasury_stock) || 0,
        cost_of_equity: parseFloat(equityForm.cost_of_equity) || 0
      };

      if (editingEquity) {
        const response = await axios.put(`${API}/equity/${editingEquity.id}`, equityData);
        // PUT returns updated equity directly
        const updatedEquity = response.data;
        setEquityRecords(equityRecords.map(e => e.id === editingEquity.id ? updatedEquity : e));
        toast.success('Equity updated successfully!');
      } else {
        const response = await axios.post(`${API}/equity`, equityData);
        // POST returns { message: "...", data: equity_data }
        const savedEquity = response.data.data || response.data;
        setEquityRecords([...equityRecords, savedEquity]);
        toast.success('Equity saved successfully!');
      }
      
      setShowEquityDialog(false);
      setEditingEquity(null);
      resetEquityForm();
      
      // Refresh equity records to ensure consistency with database
      await fetchEquityRecords();
    } catch (error) {
      console.error('Error saving equity:', error);
      toast.error('Failed to save equity');
    }
  };

  const handleEditEquity = (equity) => {
    setEditingEquity(equity);
    setEquityForm({
      authorized_shares: equity.authorized_shares || '',
      issued_shares: equity.issued_shares || '',
      par_value: equity.par_value || '',
      additional_paid_in_capital: equity.additional_paid_in_capital || '',
      retained_earnings: equity.retained_earnings || '',
      treasury_stock: equity.treasury_stock || '',
      cost_of_equity: equity.cost_of_equity || '',
      currency: equity.currency || currentPlan?.currency || 'USD',
      linked_objective_id: equity.linked_objective_id || '',
      linked_initiative_id: equity.linked_initiative_id || '',
      funding_purpose: equity.funding_purpose || '',
      investor_name: equity.investor_name || '',
      investment_date: equity.investment_date || ''
    });
    setShowEquityDialog(true);
  };

  const handleDeleteEquity = async (equityId) => {
    if (!window.confirm('Are you sure you want to delete this equity record?')) return;
    
    try {
      await axios.delete(`${API}/equity/${equityId}`);
      setEquityRecords(equityRecords.filter(e => e.id !== equityId));
      toast.success('Equity deleted successfully');
    } catch (error) {
      console.error('Error deleting equity:', error);
      toast.error('Failed to delete equity');
    }
  };

  const resetEquityForm = () => {
    setEquityForm({
      authorized_shares: '',
      issued_shares: '',
      par_value: '',
      additional_paid_in_capital: '',
      retained_earnings: '',
      treasury_stock: '',
      cost_of_equity: '',
      currency: currentPlan?.currency || 'USD',
      linked_objective_id: '',
      linked_initiative_id: '',
      funding_purpose: '',
      investor_name: '',
      investment_date: ''
    });
    setEditingEquity(null);
  };

  const resetLoanForm = () => {
    setLoanForm({
      loan_name: '',
      loan_type: 'term_loan',
      custom_loan_type_name: '',
      lender_name: '',
      principal_amount: '',
      currency: currentPlan?.currency || 'USD',
      interest_type: 'fixed',
      interest_rate: '',
      base_rate: '',
      margin: '',
      reference_rate_name: '',
      adjustment_frequency: '',
      rate_cap: '',
      rate_floor: '',
      term_months: '',
      payment_frequency: 'monthly',
      start_date: '',
      linked_objective_id: '',
      linked_initiative_id: '',
      linked_revenue_stream_id: '',
      owner: '',
      purpose: ''
    });
  };

  const handleLoanTypeSelection = (type) => {
    const typeData = loanTypes[type];
    if (typeData && type !== 'custom') {
      setLoanForm({
        ...loanForm,
        loan_type: type,
        loan_name: typeData.name,
        term_months: typeData.typical_term_months?.toString() || '',
        interest_type: typeData.typical_interest_type || 'fixed',
        payment_frequency: typeData.typical_frequency || 'monthly'
      });
    } else {
      setLoanForm({...loanForm, loan_type: type});
    }
  };

  const formatCurrency = (amount, sourceCurrency = null) => {
    if (!amount && amount !== 0) return formatCurrencyContext(0, displayCurrency || 'USD');
    // If source currency is provided, use it; otherwise use plan currency
    const currency = sourceCurrency || currentPlan?.currency || 'USD';
    return formatCurrencyContext(amount, currency, null, false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading financial management...</p>
        </div>
      </div>
    );
  }

  if (!currentPlan) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Business Plan Selected</h3>
            <p className="text-gray-600 text-sm">
              Please select or create a business plan.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate total debt with proper currency conversion
  const totalDebt = loans.reduce((sum, loan) => {
    const loanAmount = loan.principal_amount || 0;
    const loanCurrency = loan.currency || currentPlan?.currency || 'USD';
    // Convert loan amount to display currency before summing
    const convertedAmount = convertCurrency(loanAmount, loanCurrency, displayCurrency);
    return sum + convertedAmount;
  }, 0);
  
  const avgInterestRate = loans.length > 0 ? 
    loans.reduce((sum, loan) => sum + (loan.interest_rate || 0), 0) / loans.length : 0;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Financial Management</h1>
          <p className="text-gray-600 mt-1">
            Manage current debt, equity structure, and capital with strategic alignment
          </p>
        </div>
        <Badge variant="outline" className="text-sm">
          <Building2 className="h-3 w-3 mr-1" />
          {currentPlan.company_name}
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Total Debt Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Debt (in {displayCurrency})</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(totalDebt, displayCurrency)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {loans.length} loan{loans.length !== 1 ? 's' : ''} • Avg {avgInterestRate.toFixed(1)}% rate
                </p>
              </div>
              <CreditCard className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        {/* Total Equity Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Equity (in {displayCurrency})</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(
                    equityRecords.reduce((sum, eq) => {
                      const value = (parseFloat(eq.issued_shares) || 0) * (parseFloat(eq.par_value) || 0);
                      const equityCurrency = eq.currency || currentPlan?.currency || 'USD';
                      return sum + convertCurrency(value, equityCurrency, displayCurrency);
                    }, 0),
                    displayCurrency
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {equityRecords.length} equity record{equityRecords.length !== 1 ? 's' : ''}
                </p>
              </div>
              <Users className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        {/* Debt-to-Equity Ratio Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Debt-to-Equity Ratio</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(() => {
                    const totalEquityValue = equityRecords.reduce((sum, eq) => {
                      const value = (parseFloat(eq.issued_shares) || 0) * (parseFloat(eq.par_value) || 0);
                      const equityCurrency = eq.currency || currentPlan?.currency || 'USD';
                      return sum + convertCurrency(value, equityCurrency, displayCurrency);
                    }, 0);
                    const debtToEquityRatio = totalEquityValue > 0 ? totalDebt / totalEquityValue : 0;
                    return debtToEquityRatio.toFixed(2);
                  })()}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {(() => {
                    const totalEquityValue = equityRecords.reduce((sum, eq) => {
                      const value = (parseFloat(eq.issued_shares) || 0) * (parseFloat(eq.par_value) || 0);
                      const equityCurrency = eq.currency || currentPlan?.currency || 'USD';
                      return sum + convertCurrency(value, equityCurrency, displayCurrency);
                    }, 0);
                    const debtToEquityRatio = totalEquityValue > 0 ? totalDebt / totalEquityValue : 0;
                    return debtToEquityRatio < 1 ? '✅ Conservative' : 
                           debtToEquityRatio < 2 ? '⚠️ Moderate' : 
                           '❌ High Leverage';
                  })()}
                </p>
              </div>
              <PieChart className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        {/* SF Linked Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">SF Linked</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loans.filter(l => l.linked_initiative_id).length + equityRecords.filter(e => e.linked_initiative_id).length}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {loans.filter(l => l.linked_initiative_id).length} debt • {equityRecords.filter(e => e.linked_initiative_id).length} equity
                </p>
              </div>
              <Target className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="current-debt">
            <CreditCard className="h-4 w-4 mr-2" />
            Current Debt
          </TabsTrigger>
          <TabsTrigger value="current-equity">
            <PieChart className="h-4 w-4 mr-2" />
            Current Equity
          </TabsTrigger>
          <TabsTrigger value="capital-structure">
            <Building2 className="h-4 w-4 mr-2" />
            Capital Structure
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Current Debt */}
        <TabsContent value="current-debt" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Debt Portfolio</CardTitle>
                  <CardDescription>Active loans and financing obligations</CardDescription>
                </div>
                <Button onClick={openLoanDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Loan
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loans.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600">No loans recorded</p>
                  <p className="text-sm text-gray-500 mb-4">Add your first loan to track debt</p>
                  <Button onClick={openLoanDialog}>Add First Loan</Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loan</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lender</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Principal</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interest</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Term</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Initiative</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {loans.map((loan) => (
                        <tr key={loan.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-900">{loan.loan_name}</p>
                              <p className="text-xs text-gray-500">{loanTypes[loan.loan_type]?.name || loan.loan_type}</p>
                              {loan.owner && <p className="text-xs text-gray-500">Owner: {loan.owner}</p>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-900">{loan.lender_name}</td>
                          <td className="px-4 py-3 text-gray-900 font-medium">
                            {formatCurrency(loan.principal_amount, loan.currency)}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="text-xs">
                              {loan.interest_rate}% {loan.interest_type}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{loan.term_months} mo</td>
                          <td className="px-4 py-3">
                            {loan.linked_initiative_id ? (
                              <Badge variant="secondary" className="text-xs">
                                <Target className="h-3 w-3 mr-1" />
                                Linked
                              </Badge>
                            ) : (
                              <span className="text-xs text-gray-400">Not linked</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end space-x-2">
                              <Button variant="ghost" size="sm" onClick={() => handleEditLoan(loan)}>
                                <Edit3 className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteLoan(loan.id)} className="text-red-600">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 2: Current Equity */}
        <TabsContent value="current-equity" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Equity Portfolio</CardTitle>
                  <CardDescription>Ownership structure and shareholder equity</CardDescription>
                </div>
                <Button onClick={() => { resetEquityForm(); setShowEquityDialog(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Equity
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {equityRecords.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600">No equity recorded</p>
                  <p className="text-sm text-gray-500 mb-4">Add equity structure to track ownership</p>
                  <Button onClick={() => { resetEquityForm(); setShowEquityDialog(true); }}>Add First Equity</Button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {equityRecords.map((equity) => {
                    const authorizedShares = parseFloat(equity.authorized_shares || 0);
                    const issuedShares = parseFloat(equity.issued_shares || 0);
                    const parValue = parseFloat(equity.par_value || 0);
                    const additionalPaidIn = parseFloat(equity.additional_paid_in_capital || 0);
                    const retainedEarnings = parseFloat(equity.retained_earnings || 0);
                    const treasuryStock = parseFloat(equity.treasury_stock || 0);
                    
                    // Calculate component values
                    const commonStock = issuedShares * parValue;
                    const totalEquityValue = commonStock + additionalPaidIn + retainedEarnings - treasuryStock;
                    const equityCurrency = equity.currency || currentPlan?.currency || 'USD';
                    
                    return (
                      <Card key={equity.id} className="border-l-4 border-l-green-500">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <Users className="h-5 w-5 text-green-600" />
                                <h4 className="font-semibold text-gray-900">Equity Structure</h4>
                                {equityCurrency !== displayCurrency && (
                                  <Badge variant="outline" className="text-xs">
                                    {equityCurrency}
                                  </Badge>
                                )}
                              </div>
                              <div className="grid grid-cols-3 gap-4 mt-3">
                                <div>
                                  <p className="text-xs text-gray-500">Authorized Shares</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {authorizedShares.toLocaleString()}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Issued Shares</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {issuedShares.toLocaleString()}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Par Value</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {formatCurrency(parValue, equityCurrency)}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="mt-4 pt-4 border-t">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <p className="text-xs text-gray-500">Common Stock</p>
                                    <p className="text-sm font-medium text-gray-900">
                                      {formatCurrency(commonStock, equityCurrency)}
                                    </p>
                                  </div>
                                  {additionalPaidIn > 0 && (
                                    <div>
                                      <p className="text-xs text-gray-500">Additional Paid-in</p>
                                      <p className="text-sm font-medium text-gray-900">
                                        {formatCurrency(additionalPaidIn, equityCurrency)}
                                      </p>
                                    </div>
                                  )}
                                  {retainedEarnings !== 0 && (
                                    <div>
                                      <p className="text-xs text-gray-500">Retained Earnings</p>
                                      <p className="text-sm font-medium text-gray-900">
                                        {formatCurrency(retainedEarnings, equityCurrency)}
                                      </p>
                                    </div>
                                  )}
                                  {treasuryStock > 0 && (
                                    <div>
                                      <p className="text-xs text-gray-500">Treasury Stock</p>
                                      <p className="text-sm font-medium text-gray-900">
                                        {formatCurrency(treasuryStock, equityCurrency)}
                                      </p>
                                    </div>
                                  )}
                                  <div>
                                    <p className="text-xs text-gray-500">Total Equity Value</p>
                                    <p className="text-lg font-bold text-green-600">
                                      {formatCurrency(totalEquityValue, equityCurrency)}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Strategic Framework Link Indicator */}
                              {equity.linked_initiative_id && (
                                <div className="mt-3 pt-3 border-t">
                                  <Badge variant="secondary" className="text-xs">
                                    <Target className="h-3 w-3 mr-1" />
                                    Linked to Strategic Framework
                                  </Badge>
                                </div>
                              )}

                              {/* Investment Details */}
                              {(equity.investor_name || equity.funding_purpose) && (
                                <div className="mt-3 pt-3 border-t">
                                  {equity.investor_name && (
                                    <p className="text-xs text-gray-600">
                                      <span className="font-medium">Investor:</span> {equity.investor_name}
                                    </p>
                                  )}
                                  {equity.investment_date && (
                                    <p className="text-xs text-gray-600 mt-1">
                                      <span className="font-medium">Date:</span> {new Date(equity.investment_date).toLocaleDateString()}
                                    </p>
                                  )}
                                  {equity.funding_purpose && (
                                    <p className="text-xs text-gray-600 mt-1">
                                      <span className="font-medium">Purpose:</span> {equity.funding_purpose}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            <div className="flex space-x-2 ml-4">
                              <Button variant="ghost" size="sm" onClick={() => handleEditEquity(equity)}>
                                <Edit3 className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDeleteEquity(equity.id)} 
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Equity Dialog */}
          <Dialog open={showEquityDialog} onOpenChange={setShowEquityDialog}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingEquity ? 'Edit' : 'Add'} Equity Structure</DialogTitle>
                <DialogDescription>
                  Define your company's equity structure using proper accounting standards
                </DialogDescription>
              </DialogHeader>
              
              {/* Use EquityCalculator Component */}
              <EquityCalculator 
                form={equityForm} 
                setForm={setEquityForm}
                currentPlan={currentPlan}
              />
              
              {/* Currency Selector */}
              <div className="mt-4">
                <TooltipLabel htmlFor="equity-currency" tooltip="Currency for equity amounts">
                  Currency *
                </TooltipLabel>
                <Select 
                  value={equityForm.currency} 
                  onValueChange={(val) => setEquityForm({...equityForm, currency: val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((curr) => (
                      <SelectItem key={curr.value} value={curr.value}>
                        {curr.symbol} {curr.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Equity will be tracked in this currency and converted for display
                </p>
              </div>

              {/* Investment Details Section */}
              <div className="mt-6 space-y-4">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <Users className="h-4 w-4 mr-2" />
                  Investment Details
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <TooltipLabel htmlFor="investor-name" tooltip="Name of investor or funding source">
                      Investor/Source
                    </TooltipLabel>
                    <Input 
                      id="investor-name"
                      type="text"
                      value={equityForm.investor_name}
                      onChange={(e) => setEquityForm({...equityForm, investor_name: e.target.value})}
                      placeholder="e.g., Angel Investor, VC Fund"
                    />
                  </div>
                  <div>
                    <TooltipLabel htmlFor="investment-date" tooltip="Date equity was received">
                      Investment Date
                    </TooltipLabel>
                    <Input 
                      id="investment-date"
                      type="date"
                      value={equityForm.investment_date}
                      onChange={(e) => setEquityForm({...equityForm, investment_date: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <TooltipLabel htmlFor="funding-purpose" tooltip="Why this equity was raised">
                    Funding Purpose
                  </TooltipLabel>
                  <Textarea 
                    id="funding-purpose"
                    value={equityForm.funding_purpose}
                    onChange={(e) => setEquityForm({...equityForm, funding_purpose: e.target.value})}
                    placeholder="e.g., Product development, market expansion, working capital"
                    rows={2}
                  />
                </div>
              </div>

              {/* Strategic Framework Linker */}
              <div className="mt-6">
                <h4 className="font-medium text-gray-900 flex items-center mb-4">
                  <Target className="h-4 w-4 mr-2" />
                  Strategic Alignment
                </h4>
                <StrategicFrameworkLinker 
                  form={equityForm} 
                  setForm={setEquityForm} 
                  sfHierarchy={sfHierarchy}
                  label="Link to Strategic Objective/Initiative"
                  tooltip="Connect this equity investment to strategic goals it supports"
                />
              </div>
              
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => { setShowEquityDialog(false); resetEquityForm(); }}
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveEquity}>
                  {editingEquity ? 'Update' : 'Save'} Equity
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Tab 3: Capital Structure */}
        <TabsContent value="capital-structure" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Capital Structure Overview</CardTitle>
              <CardDescription>Debt-to-equity ratio and capital composition with strategic alignment</CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                // Calculate debt in display currency
                const totalDebtValue = loans.reduce((sum, loan) => {
                  const principal = parseFloat(loan.principal_amount) || 0;
                  const loanCurrency = loan.currency || currentPlan?.currency || 'USD';
                  const convertedAmount = convertCurrency(principal, loanCurrency, displayCurrency);
                  return sum + convertedAmount;
                }, 0);
                
                // Calculate equity in display currency
                const totalEquityValue = equityRecords.reduce((sum, eq) => {
                  const value = (parseFloat(eq.issued_shares) || 0) * (parseFloat(eq.par_value) || 0);
                  const equityCurrency = eq.currency || currentPlan?.currency || 'USD';
                  const convertedAmount = convertCurrency(value, equityCurrency, displayCurrency);
                  return sum + convertedAmount;
                }, 0);
                
                const totalCapital = totalDebtValue + totalEquityValue;
                
                const debtRatio = totalCapital > 0 ? (totalDebtValue / totalCapital) * 100 : 0;
                const equityRatio = totalCapital > 0 ? (totalEquityValue / totalCapital) * 100 : 0;
                const debtToEquityRatio = totalEquityValue > 0 ? totalDebtValue / totalEquityValue : 0;
                
                // WACC Calculation
                const avgCostOfDebt = loans.length > 0 
                  ? loans.reduce((sum, loan) => sum + (parseFloat(loan.interest_rate) || 0), 0) / loans.length 
                  : 0;
                const avgCostOfEquity = equityRecords.length > 0
                  ? equityRecords.reduce((sum, eq) => sum + (parseFloat(eq.cost_of_equity) || 0), 0) / equityRecords.length
                  : 0;
                const taxRate = 0.15; // Assumed tax rate - could be from plan settings
                const wacc = totalCapital > 0
                  ? ((totalDebtValue / totalCapital) * avgCostOfDebt * (1 - taxRate)) + ((totalEquityValue / totalCapital) * avgCostOfEquity)
                  : 0;

                // Chart data
                const pieChartData = [
                  { name: 'Debt', value: totalDebtValue, color: '#ef4444' },
                  { name: 'Equity', value: totalEquityValue, color: '#10b981' }
                ];

                if (totalCapital === 0) {
                  return (
                    <div className="text-center py-12">
                      <PieChart className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600 mb-2">No capital structure data</p>
                      <p className="text-sm text-gray-500">Add loans and equity information to see capital structure analysis</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-6">
                    {/* Capital Metrics with single tooltip icons */}
                    <div className="grid grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm text-gray-600 mb-1">Total Capital</p>
                              <p className="text-2xl font-bold text-gray-900">{formatCurrencyContext(totalCapital)}</p>
                              <p className="text-xs text-gray-500 mt-1">Debt + Equity</p>
                            </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-gray-400 cursor-help flex-shrink-0 ml-2" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-sm">All financing sources available to the company</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm text-gray-600 mb-1">Debt Ratio</p>
                              <p className="text-2xl font-bold text-red-600">{debtRatio.toFixed(1)}%</p>
                              <p className="text-xs text-gray-500 mt-1">{formatCurrencyContext(totalDebtValue)} debt</p>
                            </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-gray-400 cursor-help flex-shrink-0 ml-2" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-sm">Percentage of capital financed by debt. Lower = less risk</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm text-gray-600 mb-1">Equity Ratio</p>
                              <p className="text-2xl font-bold text-green-600">{equityRatio.toFixed(1)}%</p>
                              <p className="text-xs text-gray-500 mt-1">{formatCurrencyContext(totalEquityValue)} equity</p>
                            </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-gray-400 cursor-help flex-shrink-0 ml-2" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-sm">Percentage of capital financed by equity. Higher = lower risk</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm text-gray-600 mb-1">Debt-to-Equity</p>
                              <p className="text-2xl font-bold text-blue-600">{debtToEquityRatio.toFixed(2)}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {debtToEquityRatio < 1 ? '✅ Conservative' : 
                                 debtToEquityRatio < 2 ? '⚠️ Moderate' : 
                                 '❌ High Leverage'}
                              </p>
                            </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-gray-400 cursor-help flex-shrink-0 ml-2" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-sm">Financial leverage ratio. &lt;1 = Conservative, 1-2 = Moderate, &gt;2 = High</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-2 gap-6">
                      {/* Pie Chart */}
                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-base">Capital Composition</CardTitle>
                              <CardDescription>Distribution of debt vs equity</CardDescription>
                            </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-sm">Visual breakdown of total capital by financing source</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={250}>
                            <RechartsPieChart>
                              <Pie
                                data={pieChartData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {pieChartData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <RechartsTooltip 
                                formatter={(value) => formatCurrencyContext(value)}
                              />
                              <Legend />
                            </RechartsPieChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>

                      {/* Strategic Framework Analytics */}
                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-base flex items-center gap-2">
                                <Target className="h-4 w-4 text-purple-600" />
                                Strategic Framework Analytics
                              </CardTitle>
                              <CardDescription>Capital linked to strategic objectives</CardDescription>
                            </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-sm">Shows how much of your capital is aligned with strategic goals</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {(() => {
                            const linkedLoans = loans.filter(l => l.linked_objective_id || l.linked_initiative_id);
                            const linkedEquity = equityRecords.filter(e => e.linked_objective_id || e.linked_initiative_id);
                            const linkedDebtValue = linkedLoans.reduce((sum, loan) => {
                              const principal = parseFloat(loan.principal_amount) || 0;
                              const loanCurrency = loan.currency || currentPlan?.currency || 'USD';
                              return sum + convertCurrency(principal, loanCurrency, displayCurrency);
                            }, 0);
                            const linkedEquityValue = linkedEquity.reduce((sum, eq) => {
                              const value = (parseFloat(eq.issued_shares) || 0) * (parseFloat(eq.par_value) || 0);
                              const equityCurrency = eq.currency || currentPlan?.currency || 'USD';
                              return sum + convertCurrency(value, equityCurrency, displayCurrency);
                            }, 0);
                            const totalLinkedCapital = linkedDebtValue + linkedEquityValue;
                            const linkedPercentage = totalCapital > 0 ? (totalLinkedCapital / totalCapital) * 100 : 0;

                            return (
                              <div className="space-y-4">
                                {/* SF Linked Capital Summary */}
                                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-purple-900">SF-Linked Capital</span>
                                    <span className="text-lg font-bold text-purple-600">{linkedPercentage.toFixed(1)}%</span>
                                  </div>
                                  <p className="text-2xl font-bold text-purple-900">{formatCurrencyContext(totalLinkedCapital)}</p>
                                  <p className="text-xs text-purple-700 mt-1">
                                    {linkedLoans.length + linkedEquity.length} of {loans.length + equityRecords.length} items linked
                                  </p>
                                </div>

                                {/* Breakdown */}
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between p-2 bg-red-50 rounded">
                                    <div className="flex items-center gap-2">
                                      <CreditCard className="h-4 w-4 text-red-600" />
                                      <span className="text-sm text-gray-700">Linked Debt</span>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-semibold text-red-600">{formatCurrencyContext(linkedDebtValue)}</p>
                                      <p className="text-xs text-gray-600">{linkedLoans.length} of {loans.length} loans</p>
                                    </div>
                                  </div>

                                  <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                                    <div className="flex items-center gap-2">
                                      <Users className="h-4 w-4 text-green-600" />
                                      <span className="text-sm text-gray-700">Linked Equity</span>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-semibold text-green-600">{formatCurrencyContext(linkedEquityValue)}</p>
                                      <p className="text-xs text-gray-600">{linkedEquity.length} of {equityRecords.length} records</p>
                                    </div>
                                  </div>
                                </div>

                                {/* Unlinked Warning */}
                                {linkedPercentage < 50 && (
                                  <div className="flex items-start gap-2 p-2 bg-amber-50 rounded border border-amber-200">
                                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                                    <p className="text-xs text-amber-800">
                                      Less than 50% of capital is linked to strategic objectives. Consider linking capital to improve strategic alignment.
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </CardContent>
                      </Card>
                    </div>

                    {/* WACC Breakdown */}
                    <Card>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle>WACC Breakdown</CardTitle>
                            <CardDescription>Weighted Average Cost of Capital calculation</CardDescription>
                          </div>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 text-gray-400 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-sm">Average rate your company pays to finance its assets. Lower WACC = better</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-3 bg-red-50 rounded border border-red-100">
                            <div>
                              <span className="font-medium text-gray-900">Cost of Debt (after-tax)</span>
                              <p className="text-xs text-gray-600 mt-1">Avg interest rate: {avgCostOfDebt.toFixed(2)}% × (1 - {(taxRate * 100).toFixed(0)}% tax)</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-red-600">{(avgCostOfDebt * (1 - taxRate)).toFixed(2)}%</p>
                              <p className="text-xs text-gray-600">{debtRatio.toFixed(1)}% of capital</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-100">
                            <div>
                              <span className="font-medium text-gray-900">Cost of Equity</span>
                              <p className="text-xs text-gray-600 mt-1">Average required return from equity investors</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">{avgCostOfEquity.toFixed(2)}%</p>
                              <p className="text-xs text-gray-600">{equityRatio.toFixed(1)}% of capital</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between p-4 bg-blue-50 rounded border-2 border-blue-200">
                            <span className="font-bold text-gray-900 text-lg">Weighted Average Cost of Capital (WACC)</span>
                            <p className="text-2xl font-bold text-blue-600">{wacc.toFixed(2)}%</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Detailed Capital Composition - Individual Loans & Equity */}
                    <Card>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle>Detailed Capital Composition</CardTitle>
                            <CardDescription>Individual debt and equity breakdown</CardDescription>
                          </div>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 text-gray-400 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-sm">Complete list of all loans and equity with individual amounts</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-6">
                          {/* Debt Breakdown */}
                          <div>
                            <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
                              <CreditCard className="h-4 w-4 text-red-600" />
                              Debt Breakdown ({loans.length} loans)
                            </h4>
                            <div className="space-y-2">
                              {loans.map((loan, idx) => {
                                const principal = parseFloat(loan.principal_amount) || 0;
                                const loanCurrency = loan.currency || currentPlan?.currency || 'USD';
                                const convertedPrincipal = convertCurrency(principal, loanCurrency, displayCurrency);
                                const loanPercentage = totalDebtValue > 0 ? (convertedPrincipal / totalDebtValue) * 100 : 0;
                                const isLinked = loan.linked_objective_id || loan.linked_initiative_id;
                                
                                return (
                                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-900">{loan.loan_name}</span>
                                        {isLinked && (
                                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                            <Target className="h-3 w-3 mr-1" />
                                            SF Linked
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-4 mt-1">
                                        <span className="text-xs text-gray-600">{loan.lender_name}</span>
                                        <span className="text-xs text-gray-600">{loan.interest_rate}% {loan.interest_type}</span>
                                        <span className="text-xs text-gray-600">{loan.term_months} months</span>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-bold text-red-600">{formatCurrencyContext(convertedPrincipal)}</p>
                                      <p className="text-xs text-gray-600">{loanPercentage.toFixed(1)}% of debt</p>
                                    </div>
                                  </div>
                                );
                              })}
                              
                              {loans.length === 0 && (
                                <p className="text-sm text-gray-500 text-center py-4">No loans added yet</p>
                              )}
                            </div>
                          </div>

                          {/* Equity Breakdown */}
                          <div>
                            <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
                              <Users className="h-4 w-4 text-green-600" />
                              Equity Breakdown ({equityRecords.length} records)
                            </h4>
                            <div className="space-y-2">
                              {equityRecords.map((eq, idx) => {
                                const value = (parseFloat(eq.issued_shares) || 0) * (parseFloat(eq.par_value) || 0);
                                const equityCurrency = eq.currency || currentPlan?.currency || 'USD';
                                const convertedValue = convertCurrency(value, equityCurrency, displayCurrency);
                                const equityPercentage = totalEquityValue > 0 ? (convertedValue / totalEquityValue) * 100 : 0;
                                const isLinked = eq.linked_objective_id || eq.linked_initiative_id;
                                
                                return (
                                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-900">
                                          Equity Record #{idx + 1}
                                          {eq.investor_name && ` - ${eq.investor_name}`}
                                        </span>
                                        {isLinked && (
                                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                            <Target className="h-3 w-3 mr-1" />
                                            SF Linked
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-4 mt-1">
                                        <span className="text-xs text-gray-600">
                                          {parseFloat(eq.issued_shares).toLocaleString()} shares @ {formatCurrencyContext(parseFloat(eq.par_value))}
                                        </span>
                                        {eq.cost_of_equity && (
                                          <span className="text-xs text-gray-600">Cost: {eq.cost_of_equity}%</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-bold text-green-600">{formatCurrencyContext(convertedValue)}</p>
                                      <p className="text-xs text-gray-600">{equityPercentage.toFixed(1)}% of equity</p>
                                    </div>
                                  </div>
                                );
                              })}
                              
                              {equityRecords.length === 0 && (
                                <p className="text-sm text-gray-500 text-center py-4">No equity records added yet</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Loan Dialog */}
      <Dialog open={showLoanDialog} onOpenChange={setShowLoanDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLoan ? 'Edit' : 'Add'} Loan</DialogTitle>
            <DialogDescription>
              {editingLoan ? 'Update loan details' : 'Add a new loan to your debt portfolio'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveLoan} className="space-y-6">
            {/* Loan Type Selection */}
            <div>
              <TooltipLabel htmlFor="loan-type" tooltip={financialManagementTooltips.loanType}>
                Loan Type *
              </TooltipLabel>
              <Select value={loanForm.loan_type} onValueChange={handleLoanTypeSelection}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(loanTypes).map(([key, value]) => (
                    <SelectItem key={key} value={key}>
                      {value.name} - {value.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {loanForm.loan_type && loanForm.loan_type !== 'custom' && loanTypes[loanForm.loan_type] && (
                <p className="text-xs text-blue-600 mt-1">
                  Typical: {loanTypes[loanForm.loan_type].typical_term_months} months, {loanTypes[loanForm.loan_type].typical_interest_type} rate
                </p>
              )}
              
              {/* Custom Loan Type Name Field */}
              {loanForm.loan_type === 'custom' && (
                <div className="mt-3">
                  <TooltipLabel htmlFor="custom-loan-type" tooltip="Enter your custom loan type name">
                    Custom Loan Type Name *
                  </TooltipLabel>
                  <Input
                    id="custom-loan-type"
                    value={loanForm.custom_loan_type_name}
                    onChange={(e) => setLoanForm({...loanForm, custom_loan_type_name: e.target.value, loan_name: e.target.value})}
                    placeholder="e.g., Peer-to-Peer Loan, Family Loan, Government Grant"
                    required
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">Describe your custom financing type</p>
                </div>
              )}
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <TooltipLabel htmlFor="loan-name" tooltip={financialManagementTooltips.loanName}>
                  Loan Name *
                </TooltipLabel>
                <Input
                  id="loan-name"
                  value={loanForm.loan_name}
                  onChange={(e) => setLoanForm({...loanForm, loan_name: e.target.value})}
                  placeholder="e.g., ABC Bank Equipment Loan"
                  required
                />
              </div>

              <div>
                <TooltipLabel htmlFor="lender" tooltip={financialManagementTooltips.lender}>
                  Lender *
                </TooltipLabel>
                <Input
                  id="lender"
                  value={loanForm.lender_name}
                  onChange={(e) => setLoanForm({...loanForm, lender_name: e.target.value})}
                  placeholder="e.g., Commercial Bank of Ethiopia"
                  required
                />
              </div>

              <div>
                <TooltipLabel htmlFor="principal" tooltip={financialManagementTooltips.principal}>
                  Principal Amount *
                </TooltipLabel>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                    {getCurrencySymbol(loanForm.currency || currentPlan?.currency || 'USD')}
                  </span>
                  <Input
                    id="principal"
                    type="number"
                    value={loanForm.principal_amount}
                    onChange={(e) => setLoanForm({...loanForm, principal_amount: e.target.value})}
                    placeholder="e.g., 200000"
                    className="pl-8"
                    required
                  />
                </div>
              </div>

              <div>
                <TooltipLabel htmlFor="loan-currency" tooltip="Currency in which this loan is denominated">
                  Currency *
                </TooltipLabel>
                <Select value={loanForm.currency} onValueChange={(val) => setLoanForm({...loanForm, currency: val})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((curr) => (
                      <SelectItem key={curr.value} value={curr.value}>
                        {curr.symbol} {curr.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Loan will be tracked in this currency and converted for display
                </p>
              </div>

              <div>
                <TooltipLabel htmlFor="term" tooltip={financialManagementTooltips.term}>
                  Term (Months) *
                </TooltipLabel>
                <Input
                  id="term"
                  type="number"
                  value={loanForm.term_months}
                  onChange={(e) => setLoanForm({...loanForm, term_months: e.target.value})}
                  placeholder="e.g., 60"
                  required
                />
                {loanForm.term_months && (
                  <p className="text-xs text-gray-500 mt-1">{(loanForm.term_months / 12).toFixed(1)} years</p>
                )}
              </div>
            </div>

            {/* Dynamic Interest Fields */}
            <DynamicInterestFields form={loanForm} setForm={setLoanForm} />

            {/* Payment Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <TooltipLabel htmlFor="payment-frequency" tooltip={financialManagementTooltips.paymentFrequency}>
                  Payment Frequency *
                </TooltipLabel>
                <Select value={loanForm.payment_frequency} onValueChange={(val) => setLoanForm({...loanForm, payment_frequency: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="semi_annual">Semi-Annual</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <TooltipLabel htmlFor="start-date" tooltip={financialManagementTooltips.startDate}>
                  Start Date *
                </TooltipLabel>
                <Input
                  id="start-date"
                  type="date"
                  value={loanForm.start_date}
                  onChange={(e) => setLoanForm({...loanForm, start_date: e.target.value})}
                  required
                />
              </div>

              <div>
                <TooltipLabel htmlFor="owner" tooltip={financialManagementTooltips.owner}>
                  Loan Manager
                </TooltipLabel>
                <Select value={loanForm.owner || '__none__'} onValueChange={(val) => setLoanForm({...loanForm, owner: val === '__none__' ? '' : val})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No Assignment</SelectItem>
                    {teamMembers.map(member => (
                      <SelectItem key={member.id} value={member.name}>
                        {member.name} {member.role && `(${member.role})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Strategic Framework Linking - Full Cascading */}
            <StrategicFrameworkLinker 
              form={loanForm}
              setForm={setLoanForm}
              sfHierarchy={sfHierarchy}
            />

            {/* Purpose/Notes */}
            <div>
              <TooltipLabel htmlFor="purpose" tooltip="Why this loan was taken">
                Purpose / Notes
              </TooltipLabel>
              <Textarea
                id="purpose"
                value={loanForm.purpose}
                onChange={(e) => setLoanForm({...loanForm, purpose: e.target.value})}
                placeholder="e.g., Equipment purchase for production expansion"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowLoanDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingLoan ? 'Update' : 'Create'} Loan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default FinancialManagementV2;
